import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import styles from "./DetailsTemplate.module.scss";
import classNames from "classnames/bind";
import { useAppDispatch } from "~/redux/hooks";
import FormInput from "~/components/common/form/formInput/FormInput";
import OutlinedButton from "~/components/common/outlined-button/OutlinedButton";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import {
    ILineStep4FormData,
    ILinePayloadDetailsTemplate,
    IOwenStep4FormData,
    IPayloadCreateDetailsTemplate,
    IQuotePayloadDetailsTemplate,
    ISectionPayloadDetailsTemplate,
    ISectionStep4FormData,
} from "~/utils/interface/estimateSetting";
import { handleCreateDetailsTemplate } from "~/thunks/estimate-setting/estimateSettingThunk";
import { estimateSettingActions } from "~/thunks/estimate-setting/estimateSettingSlice";
import TitleEditor from "~/components/common/form/titleEdit/TitleEditor";
import DroppableArea from "~/components/common/drop-drag/DroppableArea";
import BaseBoxContent from "~/components/common/base-box-content/BaseBoxContent";
import { DragIcon } from "~/assets/icons";
import { Trash2 } from "lucide-react";
import BaseSelectCustom from "~/components/common/base-select-custom/BaseSelectCustom";
import { EstimateUoMOption } from "~/pages/admin/jobs/add-job/constants/add-estimate.constants";
import AddItem from "~/components/common/add-item/AddItem";
import { Divider } from "@mui/material";
import { notify } from "~/utils/helper";

dayjs.extend(relativeTime);
const cx = classNames.bind(styles);

interface DetailsTemplateFormData {
    templateName?: string;
    owens?: IOwenStep4FormData[];
}

interface DetailsTemplateProps {
    templateName?: string;
    templateType?: string;
    location?: string;
    onCancel?: () => void;
}

const DetailsTemplate = (props: DetailsTemplateProps) => {
    const { templateName: nameFromParent, templateType, onCancel } = props;
    const templateName = nameFromParent ?? props.templateName ?? "";
    const type = templateType ?? props.templateType ?? "";
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const [targetTab, setTargetTab] = useState(0);
    const [formData, setFormData] = useState<DetailsTemplateFormData>({
        templateName: nameFromParent ?? "",
        owens: [
            {
                title: "Good",
                sections: [
                    {
                        title: "Section Name",
                        lines: [{ itemName: "Item Name", type: "MATERIAL" }],
                    },
                ],
                quoteProfitMargin: 0,
                note: "Note",
            },
            {
                title: "Better",
                sections: [
                    {
                        title: "Section Name",
                        lines: [{ itemName: "Item Name", type: "MATERIAL" }],
                    },
                ],
                quoteProfitMargin: 0,
                note: "Note",
            },
            {
                title: "Best",
                sections: [
                    {
                        title: "Section Name",
                        lines: [{ itemName: "Item Name", type: "MATERIAL" }],
                    },
                ],
                quoteProfitMargin: 0,
                note: "Note",
            },
        ],
    });

    const mapLineToPayload = (line: ILineStep4FormData, order: number): ILinePayloadDetailsTemplate => ({
        itemName: line.itemName,
        quantity: Number(line.quantity) || 0,
        unitPrice: Number(line.price) || 0,
        itemType: line.type,
        itemUom: line.uom,
        itemOrder: order,
    });

    const mapSectionToPayload = (section: ISectionStep4FormData, sectionOrder: number): ISectionPayloadDetailsTemplate => ({
        sectionName: section.title ?? "Section Name",
        sectionOrder,
        items: (section.lines ?? []).map((line, idx) => mapLineToPayload(line, idx)),
    });

    const mapOwensToQuotes = (): IQuotePayloadDetailsTemplate[] =>
        (formData.owens ?? []).map((owen, quoteOrder) => ({
            quoteName: owen.title,
            quoteOrder,
            margin: Number(owen.quoteProfitMargin) || 0,
            note: owen.note,
            sections: (owen.sections ?? []).map((section, idx) => mapSectionToPayload(section, idx)),
        }));

    const handleAddDetails = async () => {
        if (!templateName || !type) return;
        setLoading(true);
        try {
            const payload: IPayloadCreateDetailsTemplate = {
                templateName,
                type,
                quotes: mapOwensToQuotes(),
            };
            await dispatch(handleCreateDetailsTemplate(payload)).unwrap();
            dispatch(estimateSettingActions.changeNeedRefreshDataEstimateTemplates(true));
            notify("success", "Create details template successfully");
            onCancel?.();
        } catch {
            notify("error", "Create details template failed");
        } finally {
            setLoading(false);
        }
    };

    const removeOwen = (index: number) => {
        const owens = formData.owens ?? [];
        if (owens.length <= 1) return;
        const next = owens.filter((_: IOwenStep4FormData, i: number) => i !== index);
        if (targetTab === index) setTargetTab(0);
        else if (targetTab > index) setTargetTab(targetTab - 1);
        setFormData({ ...formData, owens: next });
    };

    const handleUpdateOwen = (update: Partial<IOwenStep4FormData>, index: number) => {
        const owens = [...(formData.owens ?? [])];
        owens[index] = { ...owens[index], ...update };
        setFormData({ ...formData, owens });
    };

    const handleDropSection = (newSections: ISectionStep4FormData[]) => {
        const owens = [...(formData.owens ?? [])];
        if (!owens[targetTab]) return;
        owens[targetTab] = { ...owens[targetTab], sections: newSections };
        setFormData({ ...formData, owens });
    };

    const handleUpdateSection = (update: Partial<ISectionStep4FormData>, sectionIndex: number) => {
        const owens = [...(formData.owens ?? [])];
        const owen = owens[targetTab];
        if (!owen?.sections) return;
        owen.sections[sectionIndex] = { ...owen.sections[sectionIndex], ...update };
        setFormData({ ...formData, owens });
    };

    const handleRemoveSection = (sectionIndex: number) => {
        const owens = [...(formData.owens ?? [])];
        const owen = owens[targetTab];
        if (!owen?.sections?.length) return;
        owen.sections.splice(sectionIndex, 1);
        setFormData({ ...formData, owens });
    };

    const handleDropLines = (sectionIndex: number, newLines: ILineStep4FormData[]) => {
        const owens = [...(formData.owens ?? [])];
        const owen = owens[targetTab];
        const section = owen?.sections?.[sectionIndex];
        if (!owen?.sections || !section) return;
        owen.sections[sectionIndex] = { ...section, lines: newLines };
        setFormData({ ...formData, owens });
    };

    const handleSectionLineUpdate = (
        update: Partial<ILineStep4FormData>,
        sectionIndex: number,
        lineIndex: number
    ) => {
        const owens = [...(formData.owens ?? [])];
        const section = owens[targetTab]?.sections?.[sectionIndex];
        if (!section?.lines) return;
        section.lines[lineIndex] = { ...section.lines[lineIndex], ...update };
        setFormData({ ...formData, owens });
    };

    const handleRemoveLine = (sectionIndex: number, lineIndex: number) => {
        const owens = [...(formData.owens ?? [])];
        const section = owens[targetTab]?.sections?.[sectionIndex];
        if (!section?.lines) return;
        section.lines.splice(lineIndex, 1);
        setFormData({ ...formData, owens });
    };

    const handleAddLine = (sectionIndex: number) => {
        const owens = [...(formData.owens ?? [])];
        const owen = owens[targetTab];
        const section = owen?.sections?.[sectionIndex];
        if (!owen?.sections || !section) return;
        const lines = [...(section.lines ?? [])];
        lines.push({ itemName: "Item Name", type: "MATERIAL" });
        owen.sections[sectionIndex] = { ...section, lines };
        setFormData({ ...formData, owens });
    };

    const totalSection = (lines: ILineStep4FormData[] = []) => {
        return lines
            .reduce((sum, line) => sum + (Number(line.quantity) || 0) * (Number(line.price) || 0), 0)
            .toFixed(2);
    };

    const breakdown = useMemo(() => {
        const owen = formData.owens?.[targetTab];
        const secs = owen?.sections ?? [];
        let _mainTotal = 0;
        let _material = 0;
        let _labor = 0;
        secs.forEach((section: ISectionStep4FormData) => {
            (section.lines ?? []).forEach((line: ILineStep4FormData) => {
                const itemTotal = (Number(line.quantity) || 0) * (Number(line.price) || 0);
                _mainTotal += itemTotal;
                if (line.type === "LABOR") {
                    _labor += itemTotal;
                } else {
                    _material += itemTotal;
                }
            });
        });
        const _subTotal = _mainTotal;
        const progress = Math.max(0, Math.min(0.99, Number(owen?.quoteProfitMargin) || 0));
        const _profit = _subTotal * progress;
        const _total = _subTotal + _profit;
        const _cogs = _material + _labor;
        return {
            subTotal: _subTotal,
            total: _total,
            material: _material,
            labor: _labor,
            cogs: _cogs,
            profit: _profit,
            marginPercent: progress * 100,
        };
    }, [formData, targetTab]);

    const handleAddSection = () => {
        const owens = [...(formData.owens ?? [])];
        const owen = owens[targetTab];
        if (!owen) return;
        const sections = [...(owen.sections ?? [])];
        sections.push({
            title: "Section Name",
            lines: [{ itemName: "Item Name", type: "MATERIAL" }],
        });
        owens[targetTab] = { ...owen, sections };
        setFormData({ ...formData, owens });
    };

    return (
        <div className={cx('details-template-container')}>
            <div className={cx("template-name-block")}>
                <FormInput
                    name="Template Name"
                    text="Template Name"
                    isViewMode={false}
                    placeholder="Template Name"
                    inputkey="templateName"
                    value={formData.templateName ?? ""}
                    height="56px"
                    onChange={() => { }}
                />
            </div>

            <div className={cx("template-info")}>
                <h3 className={cx("template-info-title")}>Estimate Details                </h3>
                <div className={cx("button-container")}>
                    <OutlinedButton
                        title="Cancel"
                        onClick={() => onCancel?.()}
                        className={cx("cancel-button")}
                    />

                    <PrimaryButton
                        title="Add Details"
                        onClick={handleAddDetails}
                        className={cx("add-introduction-button")}
                        disabled={loading}
                    />
                </div>
            </div>

            <div className={cx("content")}>
                <div className={cx("quotes")}>
                    {formData.owens?.map((owen: IOwenStep4FormData, index: number) => (
                        <div
                            key={index}
                            className={cx({ selected: index === targetTab, "quote-title": true })}
                            onClick={(e) => {
                                setTargetTab(index);
                                e.stopPropagation();
                            }}
                        >
                            <button
                                type="button"
                                onClick={(e) => {
                                    removeOwen(index);
                                    e.stopPropagation();
                                }}
                            >
                                <svg
                                    width="28"
                                    height="28"
                                    viewBox="0 0 28 28"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M20 8L8 20M8 8L20 20"
                                        stroke="#FA6363"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                            <TitleEditor
                                value={owen.title ?? ""}
                                name=""
                                placeholder=""
                                inputkey=""
                                isViewMode={false}
                                onChange={(value) => handleUpdateOwen({ title: value }, index)}
                                styleView={{ fontSize: "16px", fontWeight: 500 }}
                            />
                        </div>
                    ))}
                </div>

                {formData.owens?.[targetTab] && (
                    <>
                        <DroppableArea
                            data={formData.owens?.[targetTab]?.sections ?? []}
                            onChange={(newArray: ISectionStep4FormData[]) => handleDropSection(newArray)}
                            groupName="sections"
                            renderComponent={(section: ISectionStep4FormData, sectionIndex: number) => (
                                <BaseBoxContent key={sectionIndex} className={cx("section-item")} expanded>
                                    <div data-slot="header">
                                        <div className={cx("section-title")}>
                                            <div className={cx("drag-button")}>
                                                <DragIcon className={cx("drag-icon")} />
                                            </div>
                                            <div className="flex items-center justify-between w-full">
                                                <TitleEditor
                                                    value={section.title ?? ""}
                                                    name=""
                                                    placeholder=""
                                                    inputkey=""
                                                    isViewMode={false}
                                                    onChange={(value) =>
                                                        handleUpdateSection({ title: value }, sectionIndex)
                                                    }
                                                    styleView={{ fontSize: "20px", fontWeight: 700 }}
                                                />
                                                <button
                                                    type="button"
                                                    className="w-[24px] h-[24px] cursor-pointer"
                                                    onClick={() => handleRemoveSection(sectionIndex)}
                                                >
                                                    <Trash2 color="#FA6363" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div data-slot="content">
                                        <div className={cx("section-item-table")}>
                                            <div className={cx("section-item-header")}>
                                                <div className="w-[24px]" />
                                                <div className="w-[36%]">
                                                    <h3>Item Name</h3>
                                                </div>
                                                <div className="w-[14%]">
                                                    <h3>UoM</h3>
                                                </div>
                                                <div className="w-[12%]">
                                                    <h3>Quantity</h3>
                                                </div>
                                                <div className="w-[12%]">
                                                    <h3>Price</h3>
                                                </div>
                                                <div className="w-[16%]">
                                                    <h3>Line Total</h3>
                                                </div>
                                                <div className="w-[5%]" />
                                            </div>
                                            <div className={cx("section-item-table-body")}>
                                                <DroppableArea
                                                    data={section.lines ?? []}
                                                    groupName={`sectionItems-${sectionIndex}`}
                                                    onChange={(newLines: ILineStep4FormData[]) =>
                                                        handleDropLines(sectionIndex, newLines)
                                                    }
                                                    renderComponent={(line: ILineStep4FormData, lineIndex: number) => (
                                                        <div key={lineIndex} className={cx("section-item-table-row")}>
                                                            <div className={cx("drag-button")}>
                                                                <DragIcon className={cx("drag-icon")} />
                                                            </div>
                                                            <div className="w-[36%]">
                                                                <FormInput
                                                                    name="itemName"
                                                                    text=""
                                                                    isViewMode={false}
                                                                    isViewNoInput
                                                                    placeholder=""
                                                                    inputkey="itemName"
                                                                    value={line.itemName ?? ""}
                                                                    onChange={(e) =>
                                                                        handleSectionLineUpdate(
                                                                            { itemName: e.target.value },
                                                                            sectionIndex,
                                                                            lineIndex
                                                                        )
                                                                    }
                                                                    height="31px"
                                                                />
                                                            </div>
                                                            <div className="w-[14%]">
                                                                <BaseSelectCustom
                                                                    selectedView={line.uom ?? ""}
                                                                    onViewChange={(viewId) =>
                                                                        handleSectionLineUpdate(
                                                                            { uom: viewId != null ? String(viewId) : undefined },
                                                                            sectionIndex,
                                                                            lineIndex
                                                                        )
                                                                    }
                                                                    height="36px"
                                                                    isViewMode={false}
                                                                    isViewNoInput
                                                                    viewOptions={EstimateUoMOption}
                                                                    placeholder="---"
                                                                    className={cx("select-filter")}
                                                                    minWidth="100px"
                                                                    textAlign="left"
                                                                    IconDrop={
                                                                        <div className={cx("drop")}>
                                                                            <svg
                                                                                width="24"
                                                                                height="24"
                                                                                viewBox="0 0 24 24"
                                                                                fill="none"
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                            >
                                                                                <path
                                                                                    d="M11.9994 14.308L8.19141 10.5H15.8074L11.9994 14.308Z"
                                                                                    fill="black"
                                                                                />
                                                                            </svg>
                                                                        </div>
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="w-[12%]">
                                                                <FormInput
                                                                    name="number"
                                                                    text=""
                                                                    value={line.quantity ?? ""}
                                                                    height="31px"
                                                                    isViewMode={false}
                                                                    isViewNoInput
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                                        handleSectionLineUpdate(
                                                                            { quantity: Number(e.target.value) || 0 },
                                                                            sectionIndex,
                                                                            lineIndex
                                                                        )
                                                                    }
                                                                    inputkey=""
                                                                    placeholder=""
                                                                />
                                                            </div>
                                                            <div className="w-[12%]">
                                                                <FormInput
                                                                    name="company"
                                                                    text=""
                                                                    isViewMode={false}
                                                                    isViewNoInput
                                                                    placeholder=""
                                                                    inputkey="company"
                                                                    value={line.price ?? ""}
                                                                    onChange={(e) =>
                                                                        handleSectionLineUpdate(
                                                                            {
                                                                                price: Number(e.target.value) || 0,
                                                                            },
                                                                            sectionIndex,
                                                                            lineIndex
                                                                        )
                                                                    }
                                                                    height="31px"
                                                                />
                                                            </div>
                                                            <div className="w-[16%]">
                                                                <div className={cx("total-money")}>
                                                                    <h3>
                                                                        $
                                                                        {(
                                                                            (Number(line.quantity) || 0) *
                                                                            (Number(line.price) || 0)
                                                                        ).toFixed(2)}
                                                                    </h3>
                                                                </div>
                                                            </div>
                                                            <div className="w-[5%] flex items-center">
                                                                <div
                                                                    className={cx("text-delete")}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onClick={() =>
                                                                        handleRemoveLine(sectionIndex, lineIndex)
                                                                    }
                                                                    onKeyDown={() => { }}
                                                                >
                                                                    <svg
                                                                        width="16"
                                                                        height="16"
                                                                        viewBox="0 0 16 16"
                                                                        fill="none"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                    >
                                                                        <path
                                                                            d="M3.33398 3.33398L12.6673 12.6673"
                                                                            stroke="#FA6363"
                                                                            strokeWidth="1.5"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                        <path
                                                                            d="M3.33269 12.6673L12.666 3.33398"
                                                                            stroke="#FA6363"
                                                                            strokeWidth="1.5"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div data-slot="footer">
                                        <div className={cx("section-action")}>
                                            <AddItem onClick={() => handleAddLine(sectionIndex)} />
                                        </div>
                                        <div className={cx("section-item-footer")}>
                                            <div className={cx("total")}>
                                                <h4>Section Total:</h4>
                                                <h3>${totalSection(section.lines)}</h3>
                                            </div>
                                        </div>
                                    </div>
                                </BaseBoxContent>
                            )}
                        />
                    </>
                )}

                <div className="flex items-center justify-between">
                    <Divider className="w-[calc(50%-150px)] !bg-[#1A2B47] !border-b-[#1A2B47]" />
                    <button type="button" className={cx("add-section")} onClick={handleAddSection}>
                        Add Section
                    </button>
                    <Divider className="w-[calc(50%-150px)] !bg-[#1A2B47] !border-b-[#1A2B47]" />
                </div>

                {formData.owens?.[targetTab] && (
                    <div className={cx("step4-owen-block")}>
                        <div className={cx("step4-owen-summary")}>
                            <p className={cx("step4-owen-summary-title")}>Estimate Summary</p>
                            <div className={cx("step4-owen-summary-box")}>
                                <div className={cx("step4-owen-summary-row")}>
                                    <span className={cx("step4-owen-summary-label")}>Estimate subtotal</span>
                                    <span className={cx("step4-owen-summary-value")}>$ {breakdown.subTotal.toFixed(2)}</span>
                                </div>
                                <Divider className={cx("step4-owen-summary-divider")} />
                                <div className={cx("step4-owen-summary-row", "step4-owen-summary-row-total")}>
                                    <span className={cx("step4-owen-summary-label")}>Total</span>
                                    <span className={cx("step4-owen-summary-value-total")}>$ {breakdown.total.toFixed(2)}</span>
                                </div>
                            </div>

                            <div className={cx("step4-owen-breakdown")}>
                                <div className={cx("step4-owen-breakdown-title")}>
                                    <h2>Total Breakdown</h2>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M17 11L12 6L7 11M17 18L12 13L7 18" stroke="#F9FBFE" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <div className={cx("step4-owen-breakdown-cards")}>
                                    <div className={cx("step4-owen-breakdown-card")}>
                                        <h4>Material</h4>
                                        <h2>${breakdown.material.toFixed(2)}</h2>
                                    </div>
                                    <div className={cx("step4-owen-breakdown-card")}>
                                        <h4>Labor</h4>
                                        <h2>${breakdown.labor.toFixed(2)}</h2>
                                    </div>
                                    <div className={cx("step4-owen-breakdown-card")}>
                                        <h4>COCS</h4>
                                        <h2>${breakdown.cogs.toFixed(2)}</h2>
                                    </div>
                                    <div className={cx("step4-owen-breakdown-card")}>
                                        <h4>Margin ({breakdown.marginPercent.toFixed(0)}%)</h4>
                                        <h2>${(breakdown.subTotal * (breakdown.marginPercent / 100)).toFixed(2)}</h2>
                                    </div>
                                    <div className={cx("step4-owen-breakdown-card")}>
                                        <h4>Profit</h4>
                                        <h2>${breakdown.profit.toFixed(2)}</h2>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={cx("step4-owen-notes")}>
                            <p className={cx("step4-owen-notes-title")}>Notes</p>
                            <div className={cx("step4-owen-notes-input")}>
                                <FormInput
                                    name="note"
                                    text=""
                                    placeholder="Notes"
                                    inputkey="note"
                                    value={formData.owens?.[targetTab]?.note ?? undefined}
                                    isViewMode={false}
                                    inputType="textarea"
                                    height="121px"
                                    onChange={(e) =>
                                        handleUpdateOwen({ note: e.target.value }, targetTab)
                                    }
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DetailsTemplate;